import { useEffect, useState } from "react";
import React from "react";
import type { MRT_RowSelectionState } from "material-react-table";
import { toast } from "react-toastify";

import { ViewGroup } from "../../../../utilities/types";
import type { SavedView } from "../../../../utilities/types";
import type { Props as ViewSaveDialogProps } from "../../ViewSaveDialog";
import { API } from "../../../../api";
import type { Branch, Company, Job, Contact, Region } from "../../../../models";
import { Oops } from "../../../../utilities/oops";
import { GeoJSON } from "../../../../utilities/utils";

import type { Props } from "../JobsDashboardTable.types";
import {
	dashboardStateToSavedViewState,
	savedViewStateToDashboardState,
	createDefaultSavedViews,
	generateViewId,
	getFieldNameById,
	getBranchesFromFilters,
	getRegionsFromFilters,
	buildJobFilter,
	getMultiSelectOptions,
	persistViews,
	findViewById,
	buildShareUrl,
} from "../JobsDashboardTable.helpers";
import type { DashboardStateReturn } from "./JobsDashboardState";

export interface UseCompanyViewsAndJobsParams {
	dashboardState: DashboardStateReturn;
	props: Pick<Props, "setDrawerOpenState" | "setDisplayedJobs" | "drawerOpen">;
	userContextId?: string;
	userEmail?: string;
	selectedCompany: Company | null;
	companyOptions: Company[];
	contact: Contact | undefined;
	companyContextInitialized: boolean;
}

export interface UseCompanyViewsAndJobsReturn {
	// Company (from context, passed through)
	companyOptions: Company[];
	selectedCompany: Company | null;
	branches: Branch[];
	regions: Region[];

	// Views
	views: SavedView[];
	selectedView: SavedView | null;
	inputDialogState: ViewSaveDialogProps;

	// Jobs
	jobs: Job[];
	user: Contact | undefined;
	isLoading: boolean;

	// Selection
	rowSelection: MRT_RowSelectionState;
	setRowSelection: React.Dispatch<React.SetStateAction<MRT_RowSelectionState>>;
	selectionDisplayedOnMap: MRT_RowSelectionState;

	// Dropdown lists
	jobStatusList: string[];
	testPackageList: string[];
	submitterList: string[];
	seasonList: string[];
	branchList: string[];
	regionList: string[];

	// Computed
	hasSelections: boolean;
	selectedJobs: Job[];
	unreadySelectedJobs: Job[];
	noViewSelected: boolean;
	noJobs: boolean;
	viewIsReadonly: boolean;
	saveViewDisabled: boolean;
	deleteViewDisabled: boolean;
	copyLinkDisabled: boolean;
	copyLinkTooltip: string;

	// Mobile menu
	mobileMenuElement: HTMLElement | null;
	setMobileMenuElement: (el: HTMLElement | null) => void;

	// Field accessors (for column definitions)
	getBranchField: (job: Job, b?: Branch[]) => string;
	getRegionField: (job: Job, r?: Region[]) => string;

	// Handlers
	onCompanyChanged: (company: Company | null, userData?: Contact) => Promise<void>;
	onSavedViewChanged: (event: any, value: any) => Promise<void>;
	refreshJobs: () => Promise<void>;
	showOnMap: () => Promise<void>;
	deleteView: () => void;
	saveCurrentView: () => Promise<void>;
	copyLinkToClipboard: () => Promise<void>;
	getShowMapText: () => React.ReactNode;
}

const DEFAULT_VIEWS: SavedView[] = createDefaultSavedViews(2);

export function useCompanyViewsAndJobs({
	dashboardState: ds,
	props,
	userContextId,
	userEmail,
	selectedCompany: contextSelectedCompany,
	companyOptions: contextCompanyOptions,
	contact: contextContact,
	companyContextInitialized,
}: UseCompanyViewsAndJobsParams): UseCompanyViewsAndJobsReturn {
	// #region STATE
	const [inputDialogState, setInputDialogState] = useState<ViewSaveDialogProps>({
		open: false,
		title: "",
		content: "",
		submitText: "",
		submit: () => {},
		viewName: "",
	});

	const [isLoading, setIsLoading] = useState(false);
	const companyOptions = contextCompanyOptions;
	const selectedCompany = contextSelectedCompany;
	const [views, setViews] = useState<SavedView[]>(DEFAULT_VIEWS);
	const [selectedView, setSelectedView] = useState<SavedView | null>(null);
	const [branches, setBranches] = useState<Branch[]>([]);
	const [regions, setRegions] = useState<Region[]>([]);
	const [isInitializing, setIsInitializing] = useState<boolean>(true);

	const [jobs, setJobs] = useState<Job[]>([]);
	const user = contextContact;
	const [rowSelection, setRowSelection] = useState<MRT_RowSelectionState>({});
	const [selectionDisplayedOnMap, setSelectionDisplayedOnMap] = useState<MRT_RowSelectionState>({});

	const [mobileMenuElement, setMobileMenuElement] = React.useState<null | HTMLElement>(null);

	// Dropdown lists
	const [jobStatusList, setJobStatusList] = useState<string[]>([]);
	const [testPackageList, setTestPackageList] = useState<string[]>([]);
	const [submitterList, setSubmitterList] = useState<string[]>([]);
	const [seasonList, setSeasonList] = useState<string[]>([]);
	const [branchList, setBranchList] = useState<string[]>([]);
	const [regionList, setRegionList] = useState<string[]>([]);
	// #endregion

	// #region COMPUTED
	const hasSelections = Object.keys(rowSelection).length > 0;

	const selectedJobs = Object.keys(rowSelection).map((key) => {
		const index = parseInt(key);
		if (!isNaN(index) && rowSelection[key]) {
			return jobs[index];
		}
	}) as Job[];

	const unreadySelectedJobs = selectedJobs?.filter((job) => !job?.dateFieldReady);

	const noViewSelected = !selectedView && !ds.searchParams.has("v");
	const noJobs = jobs.length === 0;
	const viewIsReadonly = !!selectedView && selectedView.group === ViewGroup.Default;
	const canSaveViewState = !viewIsReadonly && !ds.selectedViewDirty;
	const cantSaveViewState = !canSaveViewState;
	const saveViewDisabled = noViewSelected || (noJobs && cantSaveViewState);
	const deleteViewDisabled = viewIsReadonly || noViewSelected;
	const isPersonalView = !!selectedView && selectedView.group === ViewGroup.User;
	const copyLinkDisabled = noViewSelected || isPersonalView || ds.selectedViewDirty;
	const copyLinkTooltip = noViewSelected
		? "Select a view"
		: isPersonalView
			? "Save as company view to share"
			: ds.selectedViewDirty
				? "Save changes first"
				: "Copy link to clipboard";
	// #endregion

	// #region INTERNAL FUNCTIONS
	function applyMultiSelectOptions(jobs: Job[], b: Branch[], r: Region[]) {
		const options = getMultiSelectOptions(jobs, b, r);
		setJobStatusList(options.jobStatusList);
		setTestPackageList(options.testPackageList);
		setSubmitterList(options.submitterList);
		setSeasonList(options.seasonList);
		setBranchList(options.branchList);
		setRegionList(options.regionList);
	}

	function getBranchField(job: Job, b: Branch[] = []) {
		return getFieldNameById(job.branchIds, branches, b);
	}

	function getRegionField(job: Job, r: Region[] = []) {
		return getFieldNameById(job.regionIds, regions, r);
	}
	// #endregion

	// #region EFFECTS
	useEffect(() => {
		ds.updateSelectedViewDirty(selectedView);
		ds.syncUrlToState({
			selectedView,
			isInitializing,
		});
	}, [
		selectedView,
		ds.grouping,
		ds.sorting,
		ds.columnOrder,
		ds.columnVisibility,
		ds.columnFilterFns,
		ds.columnFilters,
	]);

	useEffect(() => {
		if (!companyContextInitialized || !contextSelectedCompany) {
			return;
		}
		loadCompanyData(contextSelectedCompany, contextContact);
	}, [contextSelectedCompany?.id, companyContextInitialized]);
	// #endregion

	// #region HANDLERS
	async function loadCompanyData(company: Company | null, userData?: Contact) {
		setIsLoading(true);
		try {
			setRowSelection({});

			const currentUser = userData || user;
			const userViews: SavedView[] = currentUser?.portalSettings.views ?? [];
			const newViews: SavedView[] = [...userViews];

			let branchesLocal: Branch[] = [];
			let regionsLocal: Region[] = [];
			if (company) {
				branchesLocal = await API.Data.Branches.get.byCompany(company);
				setBranches(branchesLocal);
				regionsLocal = await API.Data.Regions.get.byCompany(company);
				setRegions(regionsLocal);

				if (company.portalSettings?.views) {
					newViews.push(...company.portalSettings.views);
				}
			}
			newViews.push(...createDefaultSavedViews(branchesLocal.length));

			setViews(newViews);

			// Check for ?v=<id> param to resolve a shared view
			const viewIdParam = ds.searchParams.get("v");
			if (viewIdParam) {
				const matchingView = findViewById(newViews, viewIdParam);
				if (matchingView) {
					ds.applySavedViewState(matchingView, branchesLocal.length);
					setSelectedView(matchingView);

					if (company) {
						const dashState = savedViewStateToDashboardState(matchingView.state);
						const branchNames = getBranchesFromFilters(dashState.columnFilterValues);
						const regionNames = getRegionsFromFilters(dashState.columnFilterValues);
						const filter = buildJobFilter(company, branchNames, regionNames);
						const loadedJobs = await API.Data.Jobs.get.byFilter(filter);
						applyMultiSelectOptions(loadedJobs, branchesLocal, regionsLocal);
						setJobs(loadedJobs);
					}
				} else {
					toast.error("Shared view not found");
					setSelectedView(null);
					ds.setSearchParams(new URLSearchParams());
				}
			} else {
				setSelectedView(null);
				setJobs([]);
			}
		} catch {
			// handle error
		} finally {
			setIsLoading(false);
			setIsInitializing(false);
			setMobileMenuElement(null);
		}
	}

	async function onSavedViewChanged(event: any, value: SavedView | null) {
		event.stopPropagation();
		if (!value) {
			setSelectedView(null);
			setJobs([]);
			return;
		}

		try {
			setIsLoading(true);

			// Apply structured state directly
			ds.applySavedViewState(value, branches.length);
			setSelectedView(value);

			if (selectedCompany) {
				const dashState = savedViewStateToDashboardState(value.state);
				const branchNames = getBranchesFromFilters(dashState.columnFilterValues);
				const regionNames = getRegionsFromFilters(dashState.columnFilterValues);
				const filter = buildJobFilter(selectedCompany, branchNames, regionNames);
				const loadedJobs = await API.Data.Jobs.get.byFilter(filter);
				applyMultiSelectOptions(loadedJobs, branches, regions);
				setJobs(loadedJobs);
			}
		} catch (error) {
			Oops.handleError(error, "JobsDashboardTable.onSavedViewChanged", userEmail);
		} finally {
			setIsLoading(false);
			setMobileMenuElement(null);
		}
	}

	async function refreshJobs() {
		await loadJobs();
	}

	async function loadJobs() {
		try {
			if (!selectedCompany || (!selectedView && !ds.searchParams.has("v"))) {
				return;
			}
			setIsLoading(true);
			const branchNames = getBranchesFromFilters(ds.columnFilters);
			const regionNames = getRegionsFromFilters(ds.columnFilters);
			const filter = buildJobFilter(selectedCompany, branchNames, regionNames);
			const loadedJobs = await API.Data.Jobs.get.byFilter(filter);
			applyMultiSelectOptions(loadedJobs, branches, regions);
			setJobs(loadedJobs);
		} catch (error) {
			Oops.handleError(error, "JobsDashboardTable.loadJobs", userEmail);
		} finally {
			setIsLoading(false);
			setMobileMenuElement(null);
		}
	}

	async function updateDisplayedJobs() {
		try {
			const selected = Object.keys(rowSelection)
				.filter((key) => rowSelection[key])
				.map((key) => {
					const index = parseInt(key);
					return !isNaN(index) ? jobs[index] : undefined;
				})
				.filter(Boolean) as Job[];

			props.setDrawerOpenState(selected.length > 0);

			const jobsWithGeodata = await Promise.all(
				selected.map(async (job) => {
					const [boundary, points, zones] = await Promise.all([
						GeoJSON.getBoundary(job),
						GeoJSON.getPoints(job),
						GeoJSON.getZones(job),
					]);

					return {
						...job,
						files: {
							...job.files,
							boundaryGeoJSON: boundary ?? undefined,
							pointsGeoJSON: points ?? undefined,
							zonesGeoJSON: zones ?? undefined,
						},
					} as Job;
				}),
			);

			props.setDisplayedJobs(jobsWithGeodata);
			setSelectionDisplayedOnMap(rowSelection);

			const boundaryCount = jobsWithGeodata.filter((j) => j.files.boundaryGeoJSON).length;
			const pointCount = jobsWithGeodata.filter((j) => j.files.pointsGeoJSON).length;
			const zoneCount = jobsWithGeodata.filter((j) => j.files.zonesGeoJSON).length;
			return [boundaryCount, pointCount, zoneCount] as const;
		} catch (error) {
			Oops.handleError(error, "JobsDashboardTable.updateDisplayedJobs", userEmail);
			return [0, 0, 0] as const;
		}
	}

	async function showOnMap() {
		const loadingBoundariesToast = toast.loading("Updating map...");
		const [boundaryCount, pointCount, zoneCount] = await updateDisplayedJobs();
		toast.dismiss(loadingBoundariesToast);
		toast.info(`Displaying ${boundaryCount} boundaries, ${pointCount} point groups and ${zoneCount} zones`);
		setMobileMenuElement(null);
	}

	function getShowMapText(): React.ReactNode {
		return selectionDisplayedOnMap !== rowSelection && props.drawerOpen ? "Update Map" : "Show on Map";
	}

	function deleteView() {
		if (selectedView) {
			const newViews: SavedView[] = views.filter((v) => v.id !== selectedView.id);
			setViews(newViews);
			setSelectedView(null);

			persistViews(selectedView, newViews, userContextId, selectedCompany);
		}
		setMobileMenuElement(null);
	}

	async function copyLinkToClipboard() {
		try {
			if (!selectedView || selectedView.group === ViewGroup.User) {
				toast.info("Save as a company view to share with others");
				return;
			}

			const companyId = companyOptions.length > 1 && selectedCompany ? selectedCompany.id : undefined;
			const fullUrl = buildShareUrl(selectedView, companyId);

			await navigator.clipboard.writeText(fullUrl);
			toast.success("Link copied to clipboard");
		} catch (error) {
			Oops.handleError(error, "JobsDashboardTable.copyLinkToClipboard", userEmail);
		} finally {
			setMobileMenuElement(null);
		}
	}

	async function saveCurrentView() {
		const result = await new Promise<[string, ViewGroup] | undefined>((resolve) => {
			setInputDialogState({
				open: true,
				title: "Save current view",
				content: "Please enter a name for this view",
				submitText: "Save",
				submit: (r) => {
					resolve(r);
					setInputDialogState({ ...inputDialogState, open: false });
				},
				viewName: selectedView?.name ?? "",
			});
		});

		if (!result) {
			setMobileMenuElement(null);
			return;
		}

		const [name, type] = result;
		const now = new Date().toISOString();

		const newViews: SavedView[] = [...views];

		const currentState = dashboardStateToSavedViewState({
			sorting: ds.sorting,
			grouping: ds.grouping,
			columnOrder: ds.columnOrder,
			columnVisibility: ds.columnVisibility,
			columnFilterFunctions: ds.columnFilterFns,
			columnFilterValues: ds.columnFilters,
		});

		const isUpdating = selectedView && name === selectedView.name && type === selectedView.group;

		const newView: SavedView = {
			id: isUpdating ? selectedView.id : generateViewId(),
			name,
			group: type,
			state: currentState,
			createdAt: isUpdating ? selectedView.createdAt : now,
			updatedAt: now,
		};

		if (isUpdating) {
			const index = newViews.findIndex((v) => v.id === selectedView.id);
			if (index >= 0) newViews[index] = newView;
		} else {
			// Insert before the first view of the same group or before defaults
			const insertIndex = newViews.findIndex(
				(view) => view.group === newView.group || view.group === ViewGroup.Default,
			);
			if (insertIndex >= 0) {
				newViews.splice(insertIndex, 0, newView);
			} else {
				newViews.push(newView);
			}
		}
		setViews(newViews);
		setSelectedView(newView);

		persistViews(newView, newViews, userContextId, selectedCompany);
		setMobileMenuElement(null);
	}
	// #endregion

	return {
		// Company
		companyOptions,
		selectedCompany,
		branches,
		regions,

		// Views
		views,
		selectedView,
		inputDialogState,

		// Jobs
		jobs,
		user,
		isLoading,

		// Selection
		rowSelection,
		setRowSelection,
		selectionDisplayedOnMap,

		// Dropdown lists
		jobStatusList,
		testPackageList,
		submitterList,
		seasonList,
		branchList,
		regionList,

		// Computed
		hasSelections,
		selectedJobs,
		unreadySelectedJobs,
		noViewSelected,
		noJobs,
		viewIsReadonly,
		saveViewDisabled,
		deleteViewDisabled,
		copyLinkDisabled,
		copyLinkTooltip,

		// Mobile menu
		mobileMenuElement,
		setMobileMenuElement,

		// Field accessors
		getBranchField,
		getRegionField,

		// Handlers
		onCompanyChanged: loadCompanyData,
		onSavedViewChanged,
		refreshJobs,
		showOnMap,
		deleteView,
		saveCurrentView,
		copyLinkToClipboard,
		getShowMapText,
	};
}
